import isBoolean, { parseBoolean } from '@/utils/isBoolean';
import isValidCSSProperty from '@/utils/isValidCSSProperty';
import { isValidJsonOptions } from '@/utils/isValidJsonOptions';
import isValidRegExpString from '@/utils/isValidRegExpString';
import parseRegExp from '@/utils/parseRegExp';

const getValue = (obj, fields) => {
  if (Array.isArray(obj)) {
    return undefined;
  }

  for (const field of fields) {
    if (field === undefined || typeof field === 'symbol') {
      continue;
    }

    if (field in obj) {
      return obj[field];
    }
  }
};

/*
Class for parsing options from server and validating them. If they are not valid,
the default options should be used. If there is no option in ThemeOptionsDto interface, option won't be added
*/

export default class ObjectParser {
  _config = [];

  /*
    Method should be used in every theme to extend existing default core options
  */
  extendConfig() {
    return this;
  }

  /*
    Method parses string from field, but don't validate it
  */
  addStringParser(targetField, { defaultValue, sourceField }) {
    this._config.push((from, to) => {
      const value = getValue(from, [sourceField, targetField]);
      to[targetField] = value !== undefined ? value : defaultValue;
    });

    return this;
  }

  /*
    Method parses boolean from field,
    if string doesn't equal to boolean values - default value will be used
  */
  addBooleanParser(targetField, { defaultValue, sourceField }) {
    this._config.push((from, to) => {
      const value = getValue(from, [sourceField, targetField]);
      to[targetField] = isBoolean(value) ? parseBoolean(value) : defaultValue;
    });

    return this;
  }

  /*
    Method parses valid Css color from field,
    if string is not valid css color - default value will be used
  */
  addColorParser(targetField, { defaultValue, sourceField }) {
    this._config.push((from, to) => {
      const value = getValue(from, [sourceField, targetField]);
      to[targetField] = value !== undefined ? value : defaultValue;
    });

    return this;
  }

  addJsonSchemaParser(targetField, { defaultValue, sourceField, schemaJson }) {
    this._config.push((from, to) => {
      const value = getValue(from, [sourceField, targetField]);
      to[targetField] =
        value !== undefined && typeof value === 'string' && isValidJsonOptions(value, schemaJson)
          ? JSON.parse(value)
          : defaultValue;
    });
    return this;
  }

  /*
    Method parses number from field,
    if string can't be converted to a number - default value will be used
  */
  addNumberParser(targetField, { defaultValue, sourceField }) {
    this._config.push((from, to) => {
      const value = getValue(from, [sourceField, targetField]);
      to[targetField] =
        value !== undefined && (Number(value) || Number(value) === 0)
          ? Number(value)
          : defaultValue;
    });
    return this;
  }

  /*
    Method checks if value is valid for a given css properties (1 or more),
    if value is not valid for some properties, default value will be used
  */
  addCSSPropertiesParser(targetField, cssProperties, { defaultValue, sourceField }) {
    this._config.push((from, to) => {
      const value = getValue(from, [sourceField, targetField]);
      to[targetField] =
        value !== undefined && isValidCSSProperty(value, cssProperties) ? value : defaultValue;
    });

    return this;
  }

  addRegexpParser(targetField, { defaultValue, sourceField }) {
    this._config.push((from, to) => {
      const value = getValue(from, [sourceField, targetField]);

      let finalValue;

      if (value && isValidRegExpString(value)) {
        const { regex, flag } = parseRegExp(value);
        finalValue = new RegExp(regex, flag);
      } else if (defaultValue && isValidRegExpString(defaultValue)) {
        const { regex, flag } = parseRegExp(defaultValue);
        finalValue = new RegExp(regex, flag);
      } else {
        finalValue = defaultValue;
      }

      if (finalValue !== undefined) {
        to[targetField] = finalValue;
      }
    });

    return this;
  }

  /*
    Method parses array from field,
    if value is not valid array or can't be parsed - default value will be used
  */
  addArrayParser(targetField, { defaultValue, sourceField }) {
    this._config.push((from, to) => {
      const value = getValue(from, [sourceField, targetField]);

      let parsedArray;

      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value);
          if (Array.isArray(parsed)) {
            parsedArray = parsed;
          }
        } catch (e) {
          parsedArray = value.split(',').map(item => item.trim());
        }
      } else if (Array.isArray(value)) {
        parsedArray = value;
      }

      to[targetField] = parsedArray !== undefined ? parsedArray : defaultValue;
    });

    return this;
  }

  /*
    Method should be used when all necessary parsers will be added
  */
  parse(from) {
    const parsedObject = {};
    this._config.forEach(f => f(from, parsedObject));

    return parsedObject;
  }
}
